"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { BooruPost } from "./booru-api"
import { createClient } from "@/lib/supabase/client"

interface FavoritesStore {
  favorites: Set<string>
  favoritePosts: BooruPost[]
  isLoggedIn: boolean
  isSyncing: boolean
  addFavorite: (post: BooruPost) => void
  removeFavorite: (postId: string) => void
  isFavorite: (postId: string) => boolean
  toggleFavorite: (post: BooruPost) => boolean
  setLoggedIn: (loggedIn: boolean) => void
  syncWithDatabase: (userId: string) => Promise<void>
  migrateGuestFavorites: (userId: string) => Promise<void>
}

export const useFavoritesStore = create<FavoritesStore>()(
  persist(
    (set, get) => ({
      favorites: new Set<string>(),
      favoritePosts: [],
      isLoggedIn: false,
      isSyncing: false,

      setLoggedIn: (loggedIn: boolean) => set({ isLoggedIn: loggedIn }),

      addFavorite: async (post: BooruPost) => {
        const key = `${post.site}-${post.id}`
        const { isLoggedIn } = get()

        // Update local state first
        set((state) => {
          const newFavorites = new Set(state.favorites)
          newFavorites.add(key)

          const postExists = state.favoritePosts.some((p) => `${p.site}-${p.id}` === key)

          return {
            favorites: newFavorites,
            favoritePosts: postExists ? state.favoritePosts : [...state.favoritePosts, post],
          }
        })

        // Sync to database if logged in
        if (isLoggedIn) {
          try {
            const supabase = createClient()
            const {
              data: { user },
            } = await supabase.auth.getUser()
            if (user) {
              await supabase.from("user_favorites").upsert(
                {
                  user_id: user.id,
                  post_site: post.site,
                  post_id: String(post.id),
                  post_data: post as unknown as Record<string, unknown>,
                },
                { onConflict: "user_id,post_site,post_id" },
              )

              await supabase.from("user_activity").insert({
                user_id: user.id,
                action_type: "favorite_add",
                description: key,
                metadata: {
                  post_site: post.site,
                  post_id: String(post.id),
                  tags: post.tags || [],
                },
              })
            }
          } catch (error) {
            console.error("Failed to sync favorite to database:", error)
          }
        }
      },

      removeFavorite: async (postId: string) => {
        const { isLoggedIn } = get()
        const [site, id] = postId.split("-")
        const tags = get().favoritePosts.find((p) => `${p.site}-${p.id}` === postId)?.tags || []

        // Update local state first
        set((state) => {
          const newFavorites = new Set(state.favorites)
          newFavorites.delete(postId)
          return {
            favorites: newFavorites,
            favoritePosts: state.favoritePosts.filter((p) => `${p.site}-${p.id}` !== postId),
          }
        })

        // Sync to database if logged in
        if (isLoggedIn) {
          try {
            const supabase = createClient()
            const {
              data: { user },
            } = await supabase.auth.getUser()
            if (user) {
              await supabase
                .from("user_favorites")
                .delete()
                .eq("user_id", user.id)
                .eq("post_site", site)
                .eq("post_id", id)

              await supabase.from("user_activity").insert({
                user_id: user.id,
                action_type: "favorite_remove",
                description: postId,
                metadata: {
                  post_site: site,
                  post_id: id,
                  tags,
                },
              })
            }
          } catch (error) {
            console.error("Failed to remove favorite from database:", error)
          }
        }
      },

      isFavorite: (postId: string) => {
        return get().favorites.has(postId)
      },

      toggleFavorite: (post: BooruPost) => {
        const key = `${post.site}-${post.id}`
        const isFav = get().isFavorite(key)

        if (isFav) {
          get().removeFavorite(key)
        } else {
          get().addFavorite(post)
        }

        return !isFav
      },

      syncWithDatabase: async (userId: string) => {
        set({ isSyncing: true })
        try {
          const supabase = createClient()
          const { data: dbFavorites } = await supabase
            .from("user_favorites")
            .select("*")
            .eq("user_id", userId)
            .order("created_at", { ascending: false })

          if (dbFavorites && dbFavorites.length > 0) {
            const favorites = new Set<string>()
            const favoritePosts: BooruPost[] = []

            dbFavorites.forEach((fav: any) => {
              const key = `${fav.post_site}-${fav.post_id}`
              favorites.add(key)
              if (fav.post_data) {
                favoritePosts.push(fav.post_data as unknown as BooruPost)
              }
            })

            set({ favorites, favoritePosts, isLoggedIn: true })
          } else {
            set({ isLoggedIn: true })
          }
        } catch (error) {
          console.error("Failed to sync favorites from database:", error)
        } finally {
          set({ isSyncing: false })
        }
      },

      migrateGuestFavorites: async (userId: string) => {
        const { favoritePosts } = get()
        if (favoritePosts.length === 0) {
          set({ isLoggedIn: true })
          return
        }

        set({ isSyncing: true })
        try {
          await new Promise((resolve) => setTimeout(resolve, 500))

          const supabase = createClient()

          const inserts = favoritePosts
            .filter((post) => post.site && post.id) // Ensure both site and id exist
            .map((post) => ({
              user_id: userId,
              post_site: post.site,
              post_id: String(post.id),
              post_data: post as unknown as Record<string, unknown>,
            }))

          if (inserts.length === 0) {
            console.log("[v0] No valid favorites to migrate")
            set({ isLoggedIn: true })
            return
          }

          const { error } = await supabase
            .from("user_favorites")
            .upsert(inserts, { onConflict: "user_id,post_site,post_id" })

          if (error) {
            console.error("[v0] Upsert error:", error.message)
            // Even if migration fails, mark as logged in
            set({ isLoggedIn: true })
          } else {
            console.log("[v0] Successfully migrated", inserts.length, "favorites")
            // Now sync from database to get merged results
            await get().syncWithDatabase(userId)
          }
        } catch (error) {
          console.error("[v0] Failed to migrate guest favorites:", error)
          set({ isLoggedIn: true })
        } finally {
          set({ isSyncing: false })
        }
      },
    }),
    {
      name: "favorites",
      partialize: (state) => ({
        favorites: Array.from(state.favorites),
        favoritePosts: state.favoritePosts,
      }),
      merge: (persistedState: any, currentState) => {
        return {
          ...currentState,
          favorites: new Set(persistedState?.favorites || []),
          favoritePosts: persistedState?.favoritePosts || [],
        }
      },
    },
  ),
)
